import { Link } from 'react-router-dom';
import type { AccountSummary } from '../../lib/types';
import {
  ASSIGNMENT_LABEL,
  HEALTH_LABEL,
  HEALTH_TONE,
  IMPORTANCE_TONE,
  formatDate,
  titleCase,
} from '../../lib/format';
import { Badge, Card, CardHeader, Detail, SectionNote } from '../../components/ui';

export function AccountOverviewTab({ summary }: { summary: AccountSummary }) {
  const { account, assignments, successPlan, childAccounts, interaction } = summary;
  const metadata = Object.entries(account.metadata ?? {});

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div className="space-y-4 lg:col-span-2">
        <Card>
          <CardHeader
            title="Current position"
            description="The one-paragraph answer to “where is this account?”"
          />
          <p className="text-sm leading-relaxed whitespace-pre-wrap text-ink">
            {account.currentAccountNote ?? 'No note recorded.'}
          </p>
        </Card>

        {successPlan && (
          <Card>
            <CardHeader
              title="Success plan"
              description="What we agreed to achieve, and the rhythm we agreed to keep."
              actions={
                <Link
                  to={`/accounts/${account.id}?tab=success-plan`}
                  className="text-xs text-accent hover:underline"
                >
                  Open
                </Link>
              }
            />
            <p className="mb-3 text-sm leading-relaxed text-ink">
              {successPlan.objectiveSummary ?? 'No objective summary recorded.'}
            </p>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
              <Detail label="Response SLA">
                {successPlan.responseSlaBusinessHours
                  ? `${successPlan.responseSlaBusinessHours} business hours`
                  : 'Not set'}
              </Detail>
              <Detail label="Meeting cadence">
                {titleCase(successPlan.meetingCadenceType)}
                {successPlan.meetingCadenceDays && ` · ${successPlan.meetingCadenceDays}d`}
              </Detail>
              <Detail label="Business review">
                {titleCase(successPlan.businessReviewType)}
              </Detail>
              <Detail label="Proactive outreach">
                {successPlan.proactiveOutreachCadenceDays
                  ? `Every ${successPlan.proactiveOutreachCadenceDays} days`
                  : 'Not set'}
              </Detail>
            </dl>
          </Card>
        )}

        {!successPlan && (
          <Card>
            <CardHeader title="Success plan" />
            <SectionNote>
              No success plan yet. Without one there is no agreed cadence or response standard, so
              this account cannot appear in cadence reporting — the platform has nothing to measure
              it against.
            </SectionNote>
          </Card>
        )}

        {childAccounts.length > 0 && (
          <Card>
            <CardHeader
              title="Sub-accounts"
              count={childAccounts.length}
              description="Access to this account extends to everything beneath it."
            />
            <ul className="divide-y divide-border">
              {childAccounts.map((child) => (
                <li key={child.id} className="flex items-center justify-between gap-2 py-2">
                  <Link
                    to={`/accounts/${child.id}`}
                    className="text-sm text-accent hover:underline"
                  >
                    {child.name}
                  </Link>
                  <span className="flex items-center gap-2">
                    <Badge tone={IMPORTANCE_TONE[child.importance]} dot={false}>
                      {titleCase(child.importance)}
                    </Badge>
                    <Badge tone={HEALTH_TONE[child.health]}>{HEALTH_LABEL[child.health]}</Badge>
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>

      <div className="space-y-4">
        <Card>
          <CardHeader title="Ownership" count={assignments.length} />
          <ul className="divide-y divide-border">
            {assignments.map((assignment) => (
              <li key={assignment.id} className="py-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm text-ink">{assignment.fullName}</p>
                    <p className="text-xs text-muted">
                      {ASSIGNMENT_LABEL[assignment.assignmentType]}
                      {assignment.title && ` · ${assignment.title}`}
                    </p>
                  </div>
                  <div className="text-right">
                    {assignment.isActive ? (
                      assignment.operationalAccess ? (
                        <Badge tone="good">Active</Badge>
                      ) : (
                        <Badge tone="neutral" title="Relationship only — no operational access">
                          No access
                        </Badge>
                      )
                    ) : (
                      <Badge tone="neutral">Ended</Badge>
                    )}
                    {assignment.endsAt && (
                      <p className="mt-0.5 text-[11px] text-muted">
                        until {formatDate(assignment.endsAt)}
                      </p>
                    )}
                  </div>
                </div>
                {assignment.notes && (
                  <p className="mt-1 text-xs text-ink-2">{assignment.notes}</p>
                )}
              </li>
            ))}
          </ul>
        </Card>

        <Card>
          <CardHeader title="Interaction history" />
          <dl className="space-y-2.5">
            <Detail label="Last meaningful activity">
              {formatDate(interaction.lastMeaningfulActivityAt)}
            </Detail>
            <Detail label="Last inbound from the customer">
              {formatDate(interaction.lastCustomerContactAt)}
            </Detail>
            <Detail label="Last outbound from us">{formatDate(interaction.lastOutboundAt)}</Detail>
            <Detail label="Last business review">
              {formatDate(interaction.lastBusinessReviewAt)}
            </Detail>
          </dl>
          <SectionNote>
            Email and calendar are recorded manually until the connectors arrive in Phase 4.
          </SectionNote>
        </Card>

        {(metadata.length > 0 ||
          account.renewalOutlookLegacy !== undefined ||
          account.executiveNotes !== undefined) && (
          <Card>
            <CardHeader title="Attributes" />
            <dl className="space-y-2.5">
              {metadata.map(([key, value]) => (
                <Detail key={key} label={titleCase(key)}>
                  {String(value)}
                </Detail>
              ))}
              {/* Present only with COMMERCIAL clearance; absent for everyone else. */}
              {account.renewalOutlookLegacy !== undefined && (
                <Detail label="Renewal outlook (legacy)">
                  <span className="flex items-center gap-2">
                    {account.renewalOutlookLegacy ?? '—'}
                    <Badge tone="neutral" dot={false}>
                      Commercial
                    </Badge>
                  </span>
                </Detail>
              )}
              {account.executiveNotes !== undefined && (
                <Detail label="Executive notes">
                  <span className="flex items-start gap-2">
                    <span>{account.executiveNotes ?? '—'}</span>
                    <Badge tone="neutral" dot={false}>
                      Executive
                    </Badge>
                  </span>
                </Detail>
              )}
            </dl>
          </Card>
        )}
      </div>
    </div>
  );
}
